import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, InputAppDataService } from '@monorepo/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { from, of, throwError } from 'rxjs';
import { AppMasterDBFacade, CutbankInFacade, PersistentStorageService } from '@monorepo/core-data';
import { TCutbankInActionType } from './cutbank-in-db.models';
import * as CutbankInActions from './cutbank-in-db.actions';
import { TCutbankInLanding, TREQUEST_GET_CUTBANK_SUBJECTS } from '@monorepo/types';
import { InputAppAPIHelperUtil, InputAppErrorDisplay } from '@monorepo/utils';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class CutbankInEffects {
  constructor(
    private actions$: Actions,
    private inputAppDataService: InputAppDataService,
    private appMasterDBFacade: AppMasterDBFacade,
    private cutbankInFacade: CutbankInFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private persistentStorageService: PersistentStorageService,
    private inputAppErrorUtil: InputAppErrorDisplay,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private translate: TranslateService,
  ) {}

  async #handleHttpErrors(error: HttpErrorResponse) {
    if (error.status === 0) {
      return this.showErrorModal(
        this.translate.instant('cutbank_errors.networkErrorMessage'),
        this.translate.instant('cutbank_errors.networkErrorTitle'),
      );
    } else if (error.status === 500) {
      return this.showErrorModal(
        this.translate.instant('cutbank_errors.technicalIssueTitle'),
        this.translate.instant('cutbank_errors.technicalIssueMessage'),
      );
    }
  }

  async showErrorModal(errorTxt: string, errorHeader: string = 'Error') {
    return this.inputAppAPIHelperUtil.showPopup(errorHeader, errorTxt, undefined, 'rqc-transactions-error');
  }

  getSubjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TCutbankInActionType.GetSubjectList),
      switchMap((action: CutbankInActions.GetSubjects) =>
        from(this.inputAppErrorUtil.presentLoadingWithId(
          'FETCHING_CUT_BANK_IN_SUBJECTS',
          this.translate.instant('cutbank_common.loading_lines'),
        )).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.appMasterDBFacade.getCutbankInViewDescriptor().pipe(
          take(1),
          map((view) => {
            console.log('[VIEW DESCRIPTOR Effects]', view);
            // const subjectPayload = view?.filter((f) => f.view_name === 'ministore-landing-page');
            return [loader, action, view] as const;
          }),
        ),
      ),
      switchMap(([loader, action, view]) =>
        this.appMasterDBFacade.getSubjectKey().pipe(
          take(1),
          map((subject_key) => {
            console.log('[Subject Key Effects]', subject_key);
            return [loader, action, view, subject_key] as const;
          }),
        ),
      ),
      switchMap(([loader, action, view, subject_key]) => {
        const line_meta = (view?.find((vw) => vw.view_name === 'ministore-landing-page') as TCutbankInLanding).meta_data
          .line_meta;
        const filter_keys = line_meta.filter_keys;
        const payload: TREQUEST_GET_CUTBANK_SUBJECTS = {
          subject_key: subject_key,
          ...line_meta,
        };
        const subjectFromDB$ = from(this.persistentStorageService.get('[APP][INPUT_APP_CUTBANK_IN_SUBJECTS]'));

        return subjectFromDB$.pipe(
          take(1),
          switchMap((res) => {
            if (res) {
              this.inputAppErrorUtil.dismissLoadingWithId('FETCHING_CUT_BANK_IN_SUBJECTS');
              return of(new CutbankInActions.GetSubjectsSuccess(res));
            } else {
              return this.inputAppDataService.getCutBankSubjects(payload).pipe(
                finalize(() => {
                  this.inputAppErrorUtil.dismissLoadingWithId('FETCHING_CUT_BANK_IN_SUBJECTS');
                }),
                map((res) => {
                  const filtered_subjects = res.filter((x) => filter_keys.includes(x.filter_key));
                  this.persistentStorageService.set('[APP][INPUT_APP_CUTBANK_IN_SUBJECTS]', filtered_subjects);
                  return new CutbankInActions.GetSubjectsSuccess(filtered_subjects);
                }),
                catchError((error: HttpErrorResponse) => {
                  this.#handleHttpErrors(error);
                  return of(new CutbankInActions.GetSubjectsFailure());
                }),
              );
            }
          }),
        );
      }),
    ),
  );

  isComponentBundleScanEnabled = createEffect(() =>
    this.actions$.pipe(
      ofType(TCutbankInActionType.IsComponentBundleScanEnabled),
      switchMap((action: CutbankInActions.IsComponentBundleScanEnabled) =>
        from(this.appNotificationUtilClass.presentLoading(this.translate.instant('cutbank_common.setting_up'),)).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),

      switchMap(([loader, action]) =>
        this.appMasterDBFacade.getSubjectKey().pipe(
          take(1),
          map((subject_key) => {
            console.log('[Subject Key Effects]', subject_key);
            return [loader, action, subject_key] as const;
          }),
        ),
      ),
      switchMap(([loader, action, subject_key]) => {
        const isComponentBundleScanEnabled$ = from(
          this.persistentStorageService.get('[APP][INPUT_APP_CUTBANK_IN_COMPONENT_TAG_ENABLED]'),
        );

        return isComponentBundleScanEnabled$.pipe(
          take(1),
          switchMap((cacheData) => {
            if (typeof cacheData === 'boolean') {
              loader.dismiss();
              return of(new CutbankInActions.IsComponentBundleScanEnabledSuccess(cacheData));
            } else {
              return this.inputAppDataService.checkIfComponentScanEnabled(subject_key).pipe(
                finalize(() => {
                  loader.dismiss();
                }),

                catchError((err: HttpErrorResponse) => {
                  console.log('FACING ERROR WHILE GETTING COMP SCAN', err);
                  this.#handleHttpErrors(err);
                  return of(false);
                }),
                map((isComponenetBundleScanEnabled) => {
                  this.persistentStorageService.set(
                    '[APP][INPUT_APP_CUTBANK_IN_COMPONENT_TAG_ENABLED]',
                    isComponenetBundleScanEnabled,
                  );
                  return new CutbankInActions.IsComponentBundleScanEnabledSuccess(isComponenetBundleScanEnabled);
                }),
              );
            }
          }),
        );
      }),
    ),
  );

  getTransactionLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TCutbankInActionType.GetTransactionLog),
      switchMap((action: CutbankInActions.GetTransactionLog) =>
        from(this.appNotificationUtilClass.presentLoading(
          this.translate.instant('cutbank_common.loading_transaction_log'),
        )).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),

      switchMap(([loaderEl, action]) => {
        return this.inputAppDataService.getCutBankTransactionLog(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),

          map((res) => {
            return new CutbankInActions.GetTransactionLogSuccess(res);
          }),
          catchError((error: HttpErrorResponse) => {
            this.#handleHttpErrors(error);

            return of(new CutbankInActions.GetTransactionLogFailure());
          }),
        );
      }),
    ),
  );

  getActivityLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TCutbankInActionType.GetActivityLog),
      switchMap((action: CutbankInActions.GetActivityLog) =>
        from(this.appNotificationUtilClass.presentLoading(
          this.translate.instant('cutbank_common.loading_activity_log'),
        )).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),

      switchMap(([loaderEl, action]) => {
        return this.inputAppDataService.getCutBankInActivityLog(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            return new CutbankInActions.GetActivityLogSuccess(res);
          }),
          catchError((error: HttpErrorResponse) => {
            this.#handleHttpErrors(error);
            return of(new CutbankInActions.GetActivityLogFailure());
          }),
        );
      }),
    ),
  );
}
